// Défilement infini de vidéos, un seul lecteur par vidéo (aucune copie).
// Pas de défilement natif : chaque carte est placée par transform (GPU) selon un décalage global,
// modulo la longueur totale de la bande, donc la boucle n'a ni bout ni à-coup, dans les deux sens.
// Avance automatique continue ; au doigt ou à la souris, la bande suit le geste puis continue sur
// son élan (inertie). Un geste vertical laisse défiler la page (touch-action: pan-y).
// Seules les vidéos visibles sont lues, pour ménager la batterie et la bande passante.
if (!customElements.get('mp-videos-defilantes')) {
  class MpVideosDefilantes extends HTMLElement {
    connectedCallback() {
      this.piste = this.querySelector('[data-mp-videos-piste]');
      this.liste = this.piste?.querySelector('.mp-videos__liste');
      if (!this.piste || !this.liste) return;

      // Copies laissées par une ancienne version (deux passes) : retirées
      this.piste.querySelectorAll('.mp-videos__liste').forEach((liste) => {
        if (liste !== this.liste) liste.remove();
      });
      this.cartes = [...this.liste.children];

      this.vitesse = Number(this.dataset.vitesse) || 40; // px par seconde
      this.reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.decalage = 0;
      this.pauseJusqua = 0;
      this.elan = 0;

      this.mesurer();
      this.observerVideos();
      this.brancherGestes();

      if ('ResizeObserver' in window) {
        this.redim = new ResizeObserver(() => this.mesurer());
        this.redim.observe(this);
        this.redim.observe(this.cartes[0]);
      }

      this.dernier = performance.now();
      this.boucle = requestAnimationFrame((t) => this.avancer(t));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.boucle);
      this.observateur?.disconnect();
      this.redim?.disconnect();
    }

    // Largeur d'un pas (carte + écart), longueur totale de la bande, positions naturelles des cartes
    mesurer() {
      if (!this.cartes.length) return;
      // Feuille de style de la section chargée sans bloquer : tant qu'elle n'est pas appliquée,
      // les mesures seraient fausses (cartes pleine largeur), on attend
      if (getComputedStyle(this.piste).display !== 'flex') {
        clearTimeout(this.attenteStyle);
        this.attenteStyle = setTimeout(() => this.mesurer(), 100);
        return;
      }
      this.cartes.forEach((carte) => (carte.style.transform = ''));
      const premiere = this.cartes[0].getBoundingClientRect();
      const ecart = parseFloat(getComputedStyle(this.liste).columnGap) || 0;
      this.pas = premiere.width + ecart;
      this.total = this.pas * this.cartes.length;
      this.largeur = this.piste.clientWidth;
      const origine = this.liste.getBoundingClientRect().left;
      this.naturelles = this.cartes.map((carte) => carte.getBoundingClientRect().left - origine);
      this.debut = this.naturelles[0];
      // Boucle possible seulement si la bande dépasse l'écran d'au moins une carte
      this.peutBoucler = this.pas > 0 && this.total >= this.largeur + this.pas;
      this.placer(true);
    }

    placer(force = false) {
      if (!this.peutBoucler) {
        if (force) this.cartes.forEach((carte) => (carte.style.transform = ''));
        return;
      }
      const d = ((this.decalage % this.total) + this.total) % this.total;
      this.cartes.forEach((carte, i) => {
        let x = (i * this.pas - d + this.total) % this.total;
        // Carte sortie à gauche : elle réapparaît à droite (et inversement)
        if (x > this.total - this.pas) x -= this.total;
        const cible = this.debut + x - this.naturelles[i];
        carte.style.transform = `translate3d(${cible}px, 0, 0)`;
      });
    }

    avancer(temps) {
      const ecart = Math.min(temps - this.dernier, 64);
      this.dernier = temps;
      if (this.peutBoucler && !this.auDoigt) {
        if (Math.abs(this.elan) > 0.02) {
          // Élan après le geste : freinage progressif
          this.decalage += this.elan * ecart;
          this.elan *= Math.pow(0.95, ecart / 16);
          this.pauseJusqua = temps + 1200;
          this.placer();
        } else if (!this.reduit && temps > this.pauseJusqua && this.estVisible) {
          this.decalage += (this.vitesse * ecart) / 1000;
          this.placer();
        }
      }
      this.boucle = requestAnimationFrame((t) => this.avancer(t));
    }

    brancherGestes() {
      let departX = 0;
      let departY = 0;
      let departDecalage = 0;
      let dernierX = 0;
      let dernierT = 0;
      let sens = null; // 'h' : geste horizontal géré ici ; 'v' : on laisse défiler la page
      let idPointeur = null;

      this.piste.addEventListener('pointerdown', (event) => {
        if (!this.peutBoucler || (event.pointerType === 'mouse' && event.button !== 0)) return;
        idPointeur = event.pointerId;
        departX = dernierX = event.clientX;
        departY = event.clientY;
        departDecalage = this.decalage;
        dernierT = performance.now();
        this.elan = 0;
        this.auDoigt = true;
        sens = event.pointerType === 'mouse' ? 'h' : null;
        if (sens === 'h') {
          this.piste.setPointerCapture(idPointeur);
          this.piste.classList.add('is-glisse');
        }
      });

      this.piste.addEventListener('pointermove', (event) => {
        if (!this.auDoigt || event.pointerId !== idPointeur) return;
        const dx = event.clientX - departX;
        const dy = event.clientY - departY;
        if (!sens) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          sens = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
          if (sens === 'v') {
            this.auDoigt = false;
            return;
          }
          this.piste.setPointerCapture(idPointeur);
          this.piste.classList.add('is-glisse');
        }
        const maintenant = performance.now();
        const dt = Math.max(maintenant - dernierT, 1);
        // Vitesse du doigt (px/ms), lissée pour un élan naturel
        this.elan = 0.8 * (-(event.clientX - dernierX) / dt) + 0.2 * this.elan;
        dernierX = event.clientX;
        dernierT = maintenant;
        this.decalage = departDecalage - dx;
        this.placer();
      });

      const fin = (event) => {
        if (event.pointerId !== idPointeur) return;
        idPointeur = null;
        if (!this.auDoigt) return;
        this.auDoigt = false;
        this.piste.classList.remove('is-glisse');
        // Doigt immobile avant de lâcher : pas d'élan
        if (performance.now() - dernierT > 80) this.elan = 0;
        this.elan = Math.max(-3, Math.min(3, this.elan));
        this.pauseJusqua = performance.now() + 1200;
      };
      this.piste.addEventListener('pointerup', fin);
      this.piste.addEventListener('pointercancel', fin);
      this.piste.addEventListener('dragstart', (event) => event.preventDefault());

      // Trackpad / molette horizontale
      this.piste.addEventListener(
        'wheel',
        (event) => {
          if (!this.peutBoucler || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
          event.preventDefault();
          this.elan = 0;
          this.decalage += event.deltaX;
          this.pauseJusqua = performance.now() + 1500;
          this.placer();
        },
        { passive: false }
      );
    }

    observerVideos() {
      this.estVisible = true;
      if (!('IntersectionObserver' in window)) return;
      this.observateur = new IntersectionObserver(
        (entrees) => {
          entrees.forEach((entree) => {
            if (entree.target === this) {
              this.estVisible = entree.isIntersecting;
              return;
            }
            const video = entree.target;
            if (entree.isIntersecting) {
              video.muted = true;
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        },
        { rootMargin: '0px 200px' }
      );
      this.observateur.observe(this);
      this.querySelectorAll('video').forEach((video) => {
        video.muted = true;
        video.playsInline = true;
        this.observateur.observe(video);
      });
    }
  }
  customElements.define('mp-videos-defilantes', MpVideosDefilantes);
}
